/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { FormProvider } from "react-hook-form";
import { FlexContainer, Text } from "@nordcloud/gnui";
import { ExecutionPolicy } from "~/generated/graphql";
import { getFirstItem } from "~/tools";
import { usePlanWizard } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanProvider";
import { useFormDefaultValues } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/ActionSettingsForm/defaultFormValues";
import { useStepState } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/StepProvider";
import { usePlanSettingsFieldHandlers } from "./hooks/usePlanSettingsFieldHandlers";
import { executionPolicy, resourceOperation } from "./options";
import { PlanSettingsSections } from "./PlanSettingsSections";

type Props = {
  nextStep?: () => void;
};

export function BatchSettingsForm({ nextStep }: Props) {
  const { stepState, updateStepState } = useStepState();
  const { setPlanData } = usePlanWizard();

  const executionItem = getFirstItem(stepState.entities ?? []);
  const listId = executionItem.listId;
  const isFirstEntity = listId === stepState.selectedEntity?.listId;

  const formMethods = useFormDefaultValues(stepState);

  const { control, handleSubmit, setValue } = formMethods;
  const { handleChange, handleExecutionPolicyChange } =
    usePlanSettingsFieldHandlers({
      stepState,
      updateStepState,
      setPlanData,
      setValue,
    });

  const executionPolicyFirstAction = executionPolicy.map((policy) => ({
    ...policy,
    disabled:
      policy.value === ExecutionPolicy.SuccessOrApproval
        ? true
        : policy.disabled,
  }));

  const modifiedExecutionPolicy = isFirstEntity
    ? executionPolicyFirstAction
    : executionPolicy;

  const batchResourceOperation = resourceOperation.map((operation) => ({
    ...operation,
    disabled: operation.value,
  }));

  const submit = () => {
    nextStep?.();
  };

  return (
    <>
      <FlexContainer
        alignItems="flex-start"
        justifyContent="flex-start"
        direction="column"
      >
        <Text weight="medium">Action Batch Settings</Text>
        <FormProvider {...formMethods}>
          <form
            id="planSettingsForm"
            onSubmit={handleSubmit(submit)}
            css={{ minHeight: "13rem" }}
          >
            <PlanSettingsSections
              control={control}
              executionPolicies={modifiedExecutionPolicy}
              handleChange={handleChange}
              handleExecutionPolicyChange={handleExecutionPolicyChange}
              resourceOperations={batchResourceOperation}
            />
          </form>
        </FormProvider>
      </FlexContainer>
    </>
  );
}
